import * as Sentry from '@sentry/nextjs';

/**
 * TEAM-14430: stand in for web storage the browser won't let the page read.
 *
 * With site data blocked (Safari, Chrome or Edge "Block all cookies"), the first read of
 * `window.localStorage` or `window.sessionStorage` throws a `SecurityError`. An Android WebView
 * with DOM storage off returns `null` instead, so the first `getItem` throws a `TypeError`. That
 * first throw came from `getPersistedToken` while `OrderingApp` rendered during hydration, outside
 * every error boundary, so React tore the root down and the diner got a blank page.
 *
 * Rather than guarding every call site (webapp, SDK and the third-party `useLocalStorage` hook),
 * the body of `instrumentation-client.ts` calls {@link installStorageFallback} once per page load.
 * That runs after the file's own imports (`@/constants/envConfig` loads `@koala/sdk`) and before
 * `_app`'s module body, so ahead of the render that used to throw. Call sites that look up
 * `localStorage` or `sessionStorage` (bare or on `window`) when they run get the in-memory copy,
 * which lasts until the next full load. Anything that captured storage while modules loaded keeps
 * what it got. Today that is only the SDK's `sdkStorage` (`GenericStorage`): it already falls back
 * to its own in-memory store when storage throws, but keeps `null` storage (Android WebView), so
 * the menu cache stays off there.
 *
 * The probe only reads: a write would fire `storage` events in the diner's other tabs, a full
 * quota would make working storage look blocked, and working storage is left exactly as it was.
 * Nothing is logged to the console, because `consoleLoggingIntegration` would ship that to Sentry
 * as a log on every blocked page load; the single `storage_unavailable` warning is the signal.
 */

type StorageName = 'localStorage' | 'sessionStorage';

const STORAGE_NAMES: StorageName[] = ['localStorage', 'sessionStorage'];

const PROBE_KEY = 'storage_fallback_probe';

// Page-load state: one report per load, however many times install runs.
const activeFallbacks = new Set<StorageName>();
let reported = false;

interface StorageFailure {
  name: StorageName;
  error: unknown;
  installed: boolean;
}

/** A Map-backed `Storage`. Like the real one it stores strings, so keys and values are coerced. */
function createMemoryStorage(): Storage {
  const items = new Map<string, string>();
  return {
    get length() {
      return items.size;
    },
    getItem: (key) => items.get(String(key)) ?? null,
    setItem: (key, value) => {
      items.set(String(key), String(value));
    },
    removeItem: (key) => {
      items.delete(String(key));
    },
    clear: () => {
      items.clear();
    },
    key: (index) => Array.from(items.keys())[index] ?? null,
  };
}

/** Swap `window[name]` for an in-memory Storage. False when the browser refuses the override. */
function replaceWithMemoryStorage(name: StorageName): boolean {
  try {
    Object.defineProperty(window, name, {
      configurable: true,
      enumerable: true,
      value: createMemoryStorage(),
    });
  } catch {
    return false;
  }
  activeFallbacks.add(name);
  return true;
}

function describeError(error: unknown): { error_name: string; error_message: string } {
  return error instanceof Error
    ? { error_name: error.name, error_message: error.message }
    : { error_name: typeof error, error_message: String(error) };
}

function reportToSentry(failures: StorageFailure[]): void {
  Sentry.withScope((scope) => {
    scope.setLevel('warning');
    scope.setTag('error_class', 'storage_unavailable');
    scope.setTag('storage_fallback', failures.map(({ name }) => name).join(','));
    const details: Record<string, unknown> = {};
    for (const { name, error, installed } of failures) {
      // installed: false means the browser refused the override, so that storage still throws.
      details[name] = { ...describeError(error), installed };
    }
    scope.setContext('storage_unavailable', details);
    // Explicit fingerprint so Safari's, Chrome's and Android's different error text groups under
    // one Sentry issue.
    scope.setFingerprint(['storage_unavailable']);
    Sentry.captureException(failures[0].error);
  });
}

/**
 * Probe `localStorage` and `sessionStorage` with one read each and replace any that throws with an
 * in-memory Storage for the rest of the page load. Only lookups made after this call get the
 * replacement (see above), which is why `instrumentation-client.ts` calls it ahead of `_app`.
 */
export function installStorageFallback(): void {
  if (typeof window === 'undefined') return;

  const failures: StorageFailure[] = [];
  for (const name of STORAGE_NAMES) {
    try {
      window[name].getItem(PROBE_KEY);
    } catch (error) {
      failures.push({ name, error, installed: replaceWithMemoryStorage(name) });
    }
  }

  if (failures.length === 0 || reported) return;
  reported = true;
  reportToSentry(failures);
}

/**
 * Whether `name` was replaced with the in-memory fallback during this page load. The fallback
 * starts empty on every load, so anything that must survive a reload (such as the chunk-load
 * reload counter) has to check this.
 */
export function isStorageFallbackActive(name: StorageName): boolean {
  return activeFallbacks.has(name);
}
